import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useReveal } from "@/lib/motion";
import { CLIENTS } from "@/lib/siteData";
import { usePageMeta, NOT_FOUND_META } from "@/lib/pageMeta";
import { AutoScroller } from "@/components/AutoScroller";
import { AndreettaCase } from "@/components/case/AndreettaCase";
import { NotFound } from "@/pages/NotFound";

export const CasePage = () => {
    const { slug } = useParams();
    const client = CLIENTS.find((entry) => entry.slug === slug);
    const reveal = useReveal();

    usePageMeta(
        client
            ? {
                  title: `${client.name} — Case | RockFox Studio`,
                  description:
                      (Array.isArray(client.summary) ? client.summary[0] : client.summary) ||
                      `Projeto ${client.name} desenvolvido pela RockFox Studio`,
                  path: `/cases/${client.slug}`,
              }
            : NOT_FOUND_META,
    );

    if (!client) return <NotFound />;

    if (client.slug === "andreetta-contabilidade") {
        return <AndreettaCase client={client} />;
    }

    return (
        <article className="min-h-screen bg-white pb-28 pt-8 md:pt-12 overflow-x-hidden">
            <div className="mx-auto max-w-[1240px] px-6 sm:px-8 lg:px-12">
                {/* Client Title */}
                <motion.h1
                    className="type-display text-brand text-[42px] sm:text-[54px] md:text-[64px] font-bold leading-tight tracking-tight"
                    {...reveal()}
                >
                    {client.name}
                </motion.h1>

                {/* Summary / Description Paragraphs */}
                <motion.div
                    className="mt-6 md:mt-8 max-w-[760px] space-y-4 text-[15px] sm:text-[16.5px] leading-relaxed text-[#333333] font-sans"
                    {...reveal(0.06)}
                >
                    {Array.isArray(client.summary) ? (
                        client.summary.map((para, i) => <p key={i}>{para}</p>)
                    ) : client.summary ? (
                        <p>{client.summary}</p>
                    ) : (
                        <p className="text-ink/50 italic">
                            Resumo do projeto em desenvolvimento.
                        </p>
                    )}
                </motion.div>

                {/* Large Featured Hero Image (logo is already embedded in fundo-principal) */}
                <motion.div
                    className="mt-12 md:mt-16 relative aspect-[16/9] md:aspect-[1080/540] w-full rounded-[22px] md:rounded-[30px] overflow-hidden shadow-[0_12px_32px_rgba(0,0,0,0.08)] border border-black/5 bg-transparent"
                    {...reveal(0.1)}
                >
                    {client.featuredImage ? (
                        <img
                            src={client.featuredImage}
                            alt={`Destaque ${client.name}`}
                            className="h-full w-full object-cover scale-[1.01] transform-gpu"
                        />
                    ) : client.banner ? (
                        <img
                            src={client.banner}
                            alt={`Banner ${client.name}`}
                            className="h-full w-full object-cover scale-[1.01] transform-gpu"
                        />
                    ) : (
                        <div className="h-full w-full bg-white" />
                    )}
                </motion.div>
            </div>

            {/* Bottom Carousel of Posts Rail */}
            {client.posts && client.posts.length > 0 && (
                <motion.div className="mt-14 md:mt-20 w-full relative z-10 bg-white py-4" {...reveal(0.14)}>
                    <AutoScroller ariaLabel={`Publicações de ${client.name}`}>
                        {client.posts.map((src, index) => (
                            <div
                                key={index}
                                className="aspect-[4/5] w-[220px] sm:w-[260px] md:w-[300px] shrink-0 overflow-hidden rounded-[10px] sm:rounded-[12px] bg-white shadow-[0_6px_20px_rgba(0,0,0,0.12)] border border-black/5 transition-transform duration-300 hover:scale-[1.03]"
                            >
                                <img
                                    src={src}
                                    alt={`Publicação ${index + 1} de ${client.name}`}
                                    className="h-full w-full object-cover pointer-events-none select-none"
                                    loading="lazy"
                                    draggable="false"
                                />
                            </div>
                        ))}
                    </AutoScroller>
                </motion.div>
            )}

            {/* Bottom Centered CTA */}
            <motion.div className="mt-14 md:mt-20 flex justify-center px-6" {...reveal(0.18)}>
                <Link
                    to="/contato"
                    className="inline-flex items-center justify-center bg-brand hover:bg-brand-deep text-white font-bold uppercase tracking-wider text-[14px] sm:text-[15px] px-8 sm:px-10 py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                >
                    SOLICITE UM ORÇAMENTO!
                </Link>
            </motion.div>
        </article>
    );
};
